import { useEffect, useRef, useState } from 'react';
import { DefaultChatTransport, type ChatStatus } from 'ai';
import { useChat } from '@ai-sdk/react';
import { AlertCircle } from 'lucide-react';
import { getChatAuthHeaders } from '@/lib/chat/api';
import type { AgentUIMessage, ChatSource, PendingAutoSend } from '@/lib/chat/types';
import { ChatThread } from './ChatThread';
import { ChatComposer } from './ChatComposer';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { CapModal, parseChatCapError, type CapKind } from '@/components/CapModal';
import { analytics, capPeriodId } from '@/lib/analytics';

interface ConversationSessionProps {
  conversationId: string;
  modelId: string;
  initialMessages: AgentUIMessage[];
  totalPages: number;
  inputDisabled?: boolean;
  pendingAutoSend: PendingAutoSend | null;
  onPendingAutoSendConsumed: (requestId: string) => void;
  onMessagesChange: (messages: AgentUIMessage[]) => void;
  onConversationActivity: () => void;
  onOpenInAppSource: (source: ChatSource) => void;
  onStatusChange: (status: ChatStatus) => void;
}

export function ConversationSession({
  conversationId,
  modelId,
  initialMessages,
  totalPages,
  inputDisabled = false,
  pendingAutoSend,
  onPendingAutoSendConsumed,
  onMessagesChange,
  onConversationActivity,
  onOpenInAppSource,
  onStatusChange,
}: ConversationSessionProps) {
  const isOnline = useOnlineStatus();
  const [input, setInput] = useState('');
  const lastAutoSendRequestIdRef = useRef<string | null>(null);
  const [capModal, setCapModal] = useState<{
    open: boolean;
    kind: CapKind | null;
    used?: number;
    limit?: number;
    upgrade_url?: string;
  }>({ open: false, kind: null });

  // Track last-sent timestamp so chat_response_received can carry latency_ms.
  const lastSentAtRef = useRef<number | null>(null);

  const { messages, sendMessage, status, stop, error } = useChat<AgentUIMessage>({
    id: conversationId,
    messages: initialMessages,
    transport: new DefaultChatTransport<AgentUIMessage>({
      api: '/api/chat',
      headers: getChatAuthHeaders,
      body: () => ({
        conversationId,
        modelId,
      }),
    }),
    onFinish: () => {
      analytics.track('chat_response_received', {
        conversation_id: conversationId,
        model_id: modelId,
        latency_ms: lastSentAtRef.current ? Date.now() - lastSentAtRef.current : undefined,
      });
      lastSentAtRef.current = null;
      onConversationActivity();
    },
    onError: () => {
      // Cap denials are handled separately (cap_modal_viewed) — don't double-count.
      if (!parseChatCapError(error?.message)) {
        analytics.track('chat_response_failed', {
          conversation_id: conversationId,
          model_id: modelId,
          error_message: (error?.message || '').slice(0, 200),
        });
      }
      lastSentAtRef.current = null;
      onConversationActivity();
    },
  });

  // When useChat surfaces an error, detect the cap-denial sentinel and open
  // the modal. Other errors continue to show inline. (Codex flagged the 80%
  // pre-warning banner as overengineered — the modal at 20/20 is enough.)
  useEffect(() => {
    if (!error?.message) return;
    const parsed = parseChatCapError(error.message);
    if (parsed) {
      setCapModal({
        open: true,
        kind: parsed.kind,
        used: parsed.used,
        limit: parsed.limit,
        upgrade_url: parsed.upgrade_url,
      });
      analytics.track('cap_hit', {
        cap_type: parsed.kind,
        surface: 'chat',
        used: parsed.used,
        limit: parsed.limit,
        cap_period_id: capPeriodId(),
      });
    }
  }, [error]);

  // Auto-submit pending prompt for this specific conversation only.
  useEffect(() => {
    if (!pendingAutoSend) return;
    if (pendingAutoSend.conversationId !== conversationId) return;
    if (lastAutoSendRequestIdRef.current === pendingAutoSend.requestId) return;

    const trimmed = pendingAutoSend.text.trim();
    lastAutoSendRequestIdRef.current = pendingAutoSend.requestId;
    onPendingAutoSendConsumed(pendingAutoSend.requestId);

    if (!trimmed) return;
    lastSentAtRef.current = Date.now();
    analytics.track('chat_message_sent', {
      conversation_id: conversationId,
      model_id: modelId,
      via: 'auto_send',
      char_count: trimmed.length,
    });
    void sendMessage({ text: trimmed });
  }, [conversationId, modelId, onPendingAutoSendConsumed, pendingAutoSend, sendMessage]);

  useEffect(() => {
    onMessagesChange(messages);
  }, [messages, onMessagesChange]);

  useEffect(() => {
    onStatusChange(status as ChatStatus);
  }, [status, onStatusChange]);

  const handleSubmit = async () => {
    const trimmed = input.trim();
    if (!trimmed) return;

    setInput('');
    lastSentAtRef.current = Date.now();
    analytics.track('chat_message_sent', {
      conversation_id: conversationId,
      model_id: modelId,
      via: 'composer',
      char_count: trimmed.length,
    });
    await sendMessage({ text: trimmed });
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <ChatThread
        messages={messages}
        status={status}
        totalPages={totalPages}
        onOpenInAppSource={(source) => {
          analytics.track('chat_source_clicked', {
            conversation_id: conversationId,
            save_id: source.webpageId,
          });
          onOpenInAppSource(source);
        }}
        className="border-b border-neutral-200"
      />

      <div className="shrink-0 pt-2">
        {error && !parseChatCapError(error.message) && (
          <div className="mb-2 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5" />
            <span>{error.message}</span>
          </div>
        )}

        {!isOnline && (
          <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-700">
            You are offline. Reconnect to send messages.
          </div>
        )}

        <ChatComposer
          input={input}
          onInputChange={setInput}
          onSubmit={() => {
            void handleSubmit();
          }}
          onStop={stop}
          status={status as ChatStatus}
          hasMessages={messages.length > 0}
          disabled={!isOnline || inputDisabled}
        />
      </div>

      <CapModal
        open={capModal.open}
        onOpenChange={(open) => setCapModal((s) => ({ ...s, open }))}
        kind={capModal.kind}
        used={capModal.used}
        limit={capModal.limit}
        upgradeUrl={capModal.upgrade_url}
      />
    </div>
  );
}
